import { useState, useEffect, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useNavigate, Link } from 'react-router-dom';
import { useFastAuth, UserIntent, AccountAttributes } from '@/hooks/useFastAuth';
import { useAuth } from '@/hooks/useAuth';
import { LemonadeAvatar } from './LemonadeAvatar';
import { LemonadeProgress } from './LemonadeProgress';
import { CountdownTimer } from './CountdownTimer';
import { LemonadeProfileWizard } from '@/components/dashboard/profile/LemonadeProfileWizard';
import { useProfileWizard } from '@/contexts/ProfileWizardContext';
import { Check, ChevronLeft, Shield, AlertTriangle, Trophy, ArrowRight, Database, Zap, Clock, Sparkles, Gift, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { DateField, getDobBounds } from '@/components/shared/DateField';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { analytics } from '@/lib/analytics';
import { AuthEmailSentPanel } from './AuthEmailSentPanel';
import { SocialAuthButtons } from './SocialAuthButtons';
import {
  storeSsoTermsAcceptance,
  consumeSsoTermsAcceptance,
} from '@/lib/pivot-signup';
import { ProfileUnlockStep } from './ProfileUnlockStep';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';


interface LemonadeOnboardingProps {
  referralCode?: string | null;
  initialEmail?: string;
  sourcePage?: string;
  // REK-1924: same-origin destination to land on after signup (e.g. a survey
  // deep link). Undefined keeps the existing defaults (/dashboard in-tab, '/'
  // for the signup magic-link redirect).
  nextPath?: string;
  onSwitchToLogin: (email?: string, opts?: { alreadySent?: boolean }) => void;
  onSignupStarted?: () => void;
  onSignupComplete?: () => void;
}

type OnboardingStep = 'checking' | 'welcome' | 'products' | 'email' | 'loading' | 'reveal' | 'magic-link' | 'creating' | 'complete' | 'profile-unlock';

/** Post-signup step after the brief "complete" screen (REK-1362). */
export function resolveSignupPostCompleteStep(
  _smsFirstVerificationEnabled: boolean,
): 'profile-unlock' {
  return 'profile-unlock';
}

// Referral codes are `UPPER(SUBSTRING(MD5(user_id) FROM 1 FOR 8))` -- exactly
// 8 uppercase hex characters (handle_new_user trigger). Used to give immediate
// feedback on a mistyped code without a pre-auth DB lookup (REK-1480).
const REFERRAL_CODE_PATTERN = /^[0-9A-F]{8}$/;

/**
 * Reconcile the `ref` on the stored `reklaim_attribution` blob with the
 * referral code the user actually has in the field at signup time (REK-1480).
 *
 * - A validated `code` is written to `attr.ref` (preserving `landedAt`).
 * - A `null` code (empty field) REMOVES any existing `attr.ref`, so a `?ref=`
 *   code the user deleted does not survive in localStorage and get credited by
 *   AuthProvider's magic-link `SIGNED_IN` path. Without this, clearing the
 *   field would not truly win on the new-tab completion path.
 *
 * Returns the JSON string to persist, or `null` when the attribution blob is
 * now empty and the key should be removed. Tolerates corrupted JSON.
 */
export function reconcileAttributionRef(
  storedJson: string | null,
  code: string | null,
): string | null {
  let attr: Record<string, unknown> = {};
  try {
    const parsed = storedJson ? JSON.parse(storedJson) : {};
    if (parsed && typeof parsed === 'object') attr = parsed as Record<string, unknown>;
  } catch {
    attr = {};
  }
  if (code) {
    attr.ref = code;
    if (!attr.landedAt) attr.landedAt = new Date().toISOString();
  } else {
    delete attr.ref;
  }
  return Object.keys(attr).length > 0 ? JSON.stringify(attr) : null;
}

const PRODUCTS = [
  {
    id: 'jackpot' as UserIntent,
    icon: (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="20" cy="20" r="16" fill="#FEF3C7" />
        <text x="20" y="26" textAnchor="middle" fontSize="18">💰</text>
      </svg>
    ),
    title: 'Share your data for cash jackpots',
    description: '$1K–$10K prizes every week and month.',
    badge: 'Free',
    badgeColor: 'bg-primary/20 text-primary-foreground',
  },
  {
    id: 'exposure' as UserIntent,
    icon: (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="20" cy="20" r="16" fill="#DBEAFE" />
        <text x="20" y="26" textAnchor="middle" fontSize="18">🔍</text>
      </svg>
    ),
    title: 'See if your data is exposed',
    description: 'Breach, dark web, and data broker scans.',
    badge: 'Free',
    badgeColor: 'bg-primary/20 text-primary-foreground',
  },
  {
    id: 'rewards' as UserIntent,
    icon: (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="20" cy="20" r="16" fill="#FEF3C7" />
        <text x="20" y="26" textAnchor="middle" fontSize="18">🛡️</text>
      </svg>
    ),
    title: 'Subscribe to remove your data',
    description: 'Find and remove your data from brokers, breaches, and the dark web.',
    badge: '',
    badgeColor: '',
  },
];

export function LemonadeOnboarding({ referralCode, initialEmail, sourcePage, nextPath, onSwitchToLogin, onSignupStarted, onSignupComplete }: LemonadeOnboardingProps) {
  const navigate = useNavigate();
  const {
    isLoading: featureFlagsLoading,
    isFlagEnabled,
    error: featureFlagsError,
    flags: featureFlags,
  } = useFeatureFlags('dashboard');
  // REK-2052: sticky session gate — resolve once when flags first load; fail-closed on error.
  const [interestsStepEnabled, setInterestsStepEnabled] = useState<boolean | null>(null);
  const [skippedInterestsStep, setSkippedInterestsStep] = useState(false);
  useEffect(() => {
    if (interestsStepEnabled !== null) return;
    // Fail-closed on fetch error before waiting for data — error leaves `flags` undefined.
    if (featureFlagsError) {
      setInterestsStepEnabled(false);
      return;
    }
    // TanStack Query reports isLoading=false while auth still disables the query — wait for data.
    if (featureFlagsLoading || featureFlags === undefined) return;
    setInterestsStepEnabled(isFlagEnabled('onboarding.show_interests'));
  }, [featureFlagsLoading, featureFlagsError, featureFlags, isFlagEnabled, interestsStepEnabled]);
  const showInterestsStep = interestsStepEnabled === true;
  const interestsGatePending = interestsStepEnabled === null;

  // Profile wizard context for post-signup flow
  const { 
    remainingEntries, 
    openWizard, 
    isOpen: wizardOpen,
    closeWizard 
  } = useProfileWizard();
  const { setAccountLockedError, isAuthenticated } = useAuth();
  
  const [showProfileWizard, setShowProfileWizard] = useState(false);
  
  // REK-1732: with a prefilled email (homepage hero / marketing redirect),
  // enter the signup details form directly — do not probe existence via
  // sign-in-otp. User-declared "I already have an account" still uses WelcomeStep.
  const getInitialStep = (): OnboardingStep => {
    if (initialEmail) return 'email';
    return 'welcome';
  };
  
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(getInitialStep);
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<UserIntent[]>(
    () => PRODUCTS.map((p) => p.id)
  );

  const attribution = (() => {
    try {
      const stored = localStorage.getItem('reklaim_attribution');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  })();

  // Referral code source of truth. Seeds from the `?ref=` link (prop) or a
  // previously-stored attribution, but is also editable via the optional
  // "Have a referral code?" field on the EmailStep so users who heard a code
  // word-of-mouth (no link) can still credit a friend (REK-1480). The server
  // (`process-referral`) trims + uppercases, so we only need to pass a string.
  const [referralCodeInput, setReferralCodeInput] = useState<string>(
    referralCode || attribution.ref || '',
  );

  // Whatever is in the field at signup time wins, as long as it's well-formed
  // (the EmailStep blocks Continue on a malformed non-empty code). Empty or
  // malformed -> no referral.
  const normalizedReferralCode = referralCodeInput.trim().toUpperCase();
  const effectiveReferralCode = REFERRAL_CODE_PATTERN.test(normalizedReferralCode)
    ? normalizedReferralCode
    : null;

  const {
    step: authStep,
    email,
    lookupData,
    entriesAwarded,
    isExistingUser,
    isLoading,
    error,
    resendCooldown,
    setIntent,
    submitEmail,
    sendOtp,
    verifyOtp,
  } = useFastAuth({
    referralCode: effectiveReferralCode,
    initialEmail,
    sourcePage,
    source: attribution.source,
    medium: attribution.medium,
    campaign: attribution.campaign,
    fbclid: attribution.fbclid,
    campaignId: attribution.campaign_id,
    adsetId: attribution.adset_id,
    adId: attribution.ad_id,
    // REK-1924: forwarded as the signup magic-link `redirectTo` so clicking the
    // emailed link lands a new user on the survey deep link.
    next: nextPath,
    onAccountLocked: setAccountLockedError,
  });

  const [emailInput, setEmailInput] = useState(initialEmail || '');
  const [emailLocked, setEmailLocked] = useState(!!initialEmail);
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  // REK-1727: Marketing-consent checkbox for the Unveil retargeting funnel.
  // NOT required to proceed (`isValid` does not include it). When true,
  // consent is persisted to localStorage and the unveil-braze tag fires from
  // useFastAuth.sendOtp on the first OTP send for new users.
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Sync with auth step
  useEffect(() => {
    if (authStep === 'reveal' && currentStep === 'loading') {
      setCurrentStep('reveal');
    }
    // REK-1732: legacy multi-email-auth no longer discloses existingUser, so
    // isExistingUser stays false on that path and we keep the submitted-email
    // check-email step (no existence pivot). Clerk still surfaces its own
    // form_identifier_exists as user_exists → isExistingUser without sending
    // a code — hand off to login for that Clerk-only residual (Deferred Auth
    // UI rewrite); never reintroduce an edge-leak pivot.
    if (authStep === 'magic-link' && isExistingUser) {
      onSwitchToLogin(email || undefined, { alreadySent: false });
      return;
    }
    if (authStep === 'magic-link') {
      if (currentStep === 'creating' && error) {
        // Error during account creation - go back to magic-link step
        setCurrentStep('magic-link');
      } else if (
        currentStep !== 'magic-link' &&
        currentStep !== 'creating' &&
        currentStep !== 'complete' &&
        currentStep !== 'profile-unlock'
      ) {
        setCurrentStep('magic-link');
        // Notify parent that signup is starting - prevents auto-redirect
        onSignupStarted?.();
      }
    }
    if (authStep === 'creating' && currentStep !== 'creating' && !error) {
      setCurrentStep('creating');
    }
    if (authStep === 'complete' && currentStep !== 'complete' && currentStep !== 'profile-unlock') {
      setCurrentStep('complete');
      localStorage.removeItem('reklaim_email');
      localStorage.removeItem('reklaim_dob');
      localStorage.removeItem('reklaim_first_name');
      localStorage.removeItem('reklaim_last_name');
      localStorage.removeItem('reklaim_marketing_consent');
    }
  }, [authStep, currentStep, error, onSignupStarted, isExistingUser, email, onSwitchToLogin]);

  const handleWelcomeSelect = (value: boolean) => {
    setHasAccount(value);
    if (value) {
      onSwitchToLogin();
    } else if (interestsGatePending) {
      // Wait for sticky gate — WelcomeStep disables Continue until resolved.
      return;
    } else if (showInterestsStep) {
      setSkippedInterestsStep(false);
      setCurrentStep('products');
    } else {
      // REK-2052: skip products when flag OFF — seed all three intents (same as
      // pre-checked UI / hero initialEmail) before email.
      setSkippedInterestsStep(true);
      setIntent(PRODUCTS.map((p) => p.id));
      setCurrentStep('email');
    }
  };

  const handleProductToggle = (id: UserIntent) => {
    setSelectedProducts(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleProductsNext = () => {
    if (selectedProducts.length > 0) {
      setIntent(selectedProducts);
      analytics.trackEvent({ name: 'onboarding_step_completed', properties: { step_name: 'products', step_index: 0 } });
      setCurrentStep('email');
    }
  };

  const handleEmailSubmit = async () => {
    if (emailInput.trim()) {
      // Persist DOB for profile creation
      if (dobMonth && dobDay && dobYear) {
        const dob = `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`;
        localStorage.setItem('reklaim_dob', dob);
      }
      // Persist first/last name for profile creation
      if (firstNameInput.trim()) localStorage.setItem('reklaim_first_name', firstNameInput.trim());
      if (lastNameInput.trim()) localStorage.setItem('reklaim_last_name', lastNameInput.trim());
      // REK-1480: reconcile `reklaim_attribution.ref` with the referral field's
      // value at submit so BOTH completion paths agree on the referral. The
      // same-tab OTP path reads the code from `useFastAuth` options, but the
      // magic-link-in-new-tab path is handled by AuthProvider's SIGNED_IN
      // handler, which only sees the code via localStorage attribution. A
      // validated code is stored; a cleared field REMOVES any stale `ref`
      // (e.g. a `?ref=` code the user deleted) so it isn't credited on the
      // new-tab path. `effectiveReferralCode` is already validated against
      // REFERRAL_CODE_PATTERN, and the EmailStep blocks Continue on a malformed
      // non-empty code.
      const nextAttribution = reconcileAttributionRef(
        localStorage.getItem('reklaim_attribution'),
        effectiveReferralCode,
      );
      if (nextAttribution) {
        localStorage.setItem('reklaim_attribution', nextAttribution);
      } else {
        localStorage.removeItem('reklaim_attribution');
      }
      // REK-1727: ALWAYS overwrite on submit (never `if (marketingConsent)`)
      // so a stale 'true' from a prior session can never be read by
      // useFastAuth.verifyOtp later. The user_consents row is only
      // written on new signups when this key === 'true'.
      localStorage.setItem('reklaim_marketing_consent', String(marketingConsent));
      // PostHog-only event fired when the consent box is checked at submit
      // time. The downstream Braze profile creation + unveil_start event
      // happens server-side in unveil-braze (invoked from useFastAuth.sendOtp).
      if (marketingConsent) {
        analytics.trackEvent({
          name: 'unveil_marketing_opt_in',
          properties: { source_page: sourcePage || 'onboarding' },
        });
      }
      const proceeded = await submitEmail(emailInput.trim());
      // submitEmail returns false when NeverBounce rejected the email (or the
      // lookup errored). The hook has already reset to the 'email' step with an
      // inline error, so stay on the form and do NOT advance to the scan.
      if (!proceeded) return;
      analytics.trackEvent({ name: 'onboarding_step_completed', properties: { step_name: 'email', step_index: 1 } });
      setCurrentStep('loading');
    }
  };

  // Cross-tab magic-link click: AuthProvider already updates isAuthenticated
  // when the user clicks the link in another tab; route to dashboard so the
  // current tab catches up. Replaces the prior direct supabase.auth subscription
  // inside MagicLinkStep (per the auth-abstraction rule -- shared components
  // never import the Supabase client).
  useEffect(() => {
    if (currentStep === 'magic-link' && isAuthenticated) {
      navigate(nextPath ?? '/dashboard');
    }
  }, [currentStep, isAuthenticated, navigate, nextPath]);

  useEffect(() => {
    if (currentStep !== 'complete') return;
    const timer = window.setTimeout(
      () => setCurrentStep(resolveSignupPostCompleteStep(false)),
      1200,
    );
    return () => window.clearTimeout(timer);
  }, [currentStep]);

  const canGoBack =
    !initialEmail &&
    (currentStep === 'products' || currentStep === 'email');

  const handleBack = () => {
    if (currentStep === 'products') setCurrentStep('welcome');
    // REK-2052: when interests are gated OFF, skip back over the hidden products step.
    if (currentStep === 'email') {
      setCurrentStep(skippedInterestsStep ? 'welcome' : 'products');
    }
  };

  const getProgressStep = () => {
    if (['checking', 'welcome', 'products', 'email'].includes(currentStep)) return 0;
    if (['loading', 'reveal'].includes(currentStep)) return 1;
    return 2;
  };

  return (
    <div className="flex-1 flex">
      {/* Left Sidebar - Progress (desktop only) */}
      <div className="hidden lg:flex w-64 shrink-0 pl-16 pt-20">
        <LemonadeProgress 
          currentStep={getProgressStep()} 
          steps={['About you', 'Your data', 'Your Results']} 
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-start pt-6 lg:pt-12 px-4 relative">
        {/* Back Button */}
        {canGoBack && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleBack}
            aria-label="Back"
            className="absolute left-4 top-6 lg:left-8 lg:top-12 size-8 p-1.5 rounded-sm"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}

        {/* Avatar - hidden on reveal step to save space */}
        {currentStep !== 'reveal' && <LemonadeAvatar />}

        {/* Content based on step */}
        <div className={`w-full mt-8 ${currentStep === 'reveal' ? 'max-w-6xl' : 'max-w-lg'}`}>
          {currentStep === 'checking' && <CheckingStep />}

          {currentStep === 'welcome' && (
            <WelcomeStep
              onSelect={handleWelcomeSelect}
              continueDisabled={interestsGatePending}
            />
          )}

          {currentStep === 'products' && (
            <ProductsStep
              products={PRODUCTS}
              selected={selectedProducts}
              onToggle={handleProductToggle}
              onNext={handleProductsNext}
              canProceed={selectedProducts.length > 0}
            />
          )}

          {currentStep === 'email' && (
            <EmailStep
              firstName={firstNameInput}
              lastName={lastNameInput}
              email={emailInput}
              emailReadOnly={emailLocked}
              onClearReadOnly={() => setEmailLocked(false)}
              dobMonth={dobMonth}
              dobDay={dobDay}
              dobYear={dobYear}
              ageConfirmed={ageConfirmed}
              marketingConsent={marketingConsent}
              referralCode={referralCodeInput}
              onReferralCodeChange={setReferralCodeInput}
              onFirstNameChange={setFirstNameInput}
              onLastNameChange={setLastNameInput}
              onEmailChange={setEmailInput}
              onDobMonthChange={setDobMonth}
              onDobDayChange={setDobDay}
              onDobYearChange={setDobYear}
              onAgeConfirmedChange={setAgeConfirmed}
              onMarketingConsentChange={setMarketingConsent}
              onSubmit={handleEmailSubmit}
              isLoading={isLoading}
              error={error}
            />
          )}

          {currentStep === 'loading' && <LoadingStep />}

          {currentStep === 'reveal' && lookupData && (
            <RevealStep
              email={email || initialEmail || ''}
              breachData={lookupData.breachData}
              profileData={lookupData.profileData}
              entriesAwarded={entriesAwarded}
              calculation={lookupData.calculation}
              brandCount={lookupData.brandCount ?? 7}
              dataBrokerCount={lookupData.dataBrokerCount ?? 5}
              accountAttributes={lookupData.accountAttributes}
              hasUnveilData={lookupData.hasUnveilData}
              // Keep the claim CTA disabled once the OTP request has completed
              // (`authStep === 'magic-link'`). For existing users the redirect
              // effect fires on the next render to send them to login, but
              // until then `currentStep` stays on `reveal` and `isLoading` has
              // already cleared — without this guard a quick second click
              // would call `sendOtp` again and trigger a duplicate OTP email.
              isLoading={isLoading || authStep === 'magic-link'}
              onClaim={() => {
                analytics.trackEvent({ name: 'onboarding_step_completed', properties: { step_name: 'reveal', step_index: 2 } });
                sendOtp();
              }}
            />
          )}

          {currentStep === 'magic-link' && (
            <MagicLinkStep
              email={email || initialEmail || ''}
              onResend={sendOtp}
              onVerifyOtp={verifyOtp}
              isLoading={isLoading}
              error={error}
              resendCooldown={resendCooldown}
            />
          )}

          {currentStep === 'creating' && <CreatingStep />}

          {currentStep === 'complete' && lookupData && (
            <CompleteStep 
              earnEntries={lookupData.calculation.base + lookupData.calculation.profileBonus}
            />
          )}
          {currentStep === 'complete' && !lookupData && (
            <CompleteStep earnEntries={entriesAwarded} />
          )}

          {currentStep === 'profile-unlock' && (
            <ProfileUnlockStep 
              remainingEntries={remainingEntries}
              onStartWizard={() => setShowProfileWizard(true)}
              onSkip={() => {
                onSignupComplete?.();
              }}
            />
          )}
        </div>
      </div>
      
      {/* Profile Wizard Overlay */}
      {showProfileWizard && (
        <LemonadeProfileWizard 
          isOpen={showProfileWizard} 
          onClose={() => {
            setShowProfileWizard(false);
            onSignupComplete?.();
          }} 
        />
      )}
    </div>
  );
}

// Sub-components

export function WelcomeStep({
  onSelect,
  continueDisabled = false,
}: {
  onSelect: (hasAccount: boolean) => void;
  continueDisabled?: boolean;
}) {
  const [selected, setSelected] = useState<boolean | null>(null);
  const continueBlocked = selected === null || (selected === false && continueDisabled);

  return (
    <div className="text-left space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Create your account
        </h1>
        <p className="text-lg text-muted-foreground">
          Do you already have a Reklaim account?
        </p>
      </div>

      {/* Radiogroup with custom-styled radio cards. `role="radio"` +
       * `aria-checked` are preserved on the shadcn `<Button>` to match the
       * pre-Phase-6 raw `<button>` implementation byte-for-byte. The cards
       * support Tab + Space/Enter (the default Button interactions); full
       * radiogroup arrow-key roving focus is NOT implemented here -- a
       * subsequent refactor to shadcn `<RadioGroup>` + `<RadioGroupItem>`
       * tracked under 6d will pick up the canonical Radix roving-tabIndex +
       * arrow-key behavior. */}
      <div className="space-y-3" role="radiogroup" aria-label="Account status">
        <Button
          type="button"
          variant="ghost"
          role="radio"
          aria-checked={selected === true}
          onClick={() => setSelected(true)}
          className={`h-auto w-full items-center justify-start gap-4 whitespace-normal rounded-lg border-2 p-4 text-left font-normal transition-colors hover:text-foreground ${
            selected === true
              ? 'border-primary bg-primary/5 hover:bg-primary/5'
              : 'border-border bg-background hover:border-muted-foreground hover:bg-background'
          }`}
        >
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            selected === true ? 'border-primary bg-primary' : 'border-muted-foreground'
          }`}>
            {selected === true && <div className="w-2 h-2 bg-primary-foreground rounded-full" />}
          </div>
          <span className="text-lg font-medium text-foreground">Yes, I have an account</span>
        </Button>

        <Button
          type="button"
          variant="ghost"
          role="radio"
          aria-checked={selected === false}
          onClick={() => setSelected(false)}
          className={`h-auto w-full items-center justify-start gap-4 whitespace-normal rounded-lg border-2 p-4 text-left font-normal transition-colors hover:text-foreground ${
            selected === false
              ? 'border-primary bg-primary/5 hover:bg-primary/5'
              : 'border-border bg-background hover:border-muted-foreground hover:bg-background'
          }`}
        >
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            selected === false ? 'border-primary bg-primary' : 'border-muted-foreground'
          }`}>
            {selected === false && <div className="w-2 h-2 bg-primary-foreground rounded-full" />}
          </div>
          <span className="text-lg font-medium text-foreground">No, I'm new here</span>
        </Button>
      </div>

      <Button
        size="lg"
        onClick={() => !continueBlocked && onSelect(selected!)}
        disabled={continueBlocked}
        className="w-full h-14 text-lg"
      >
        Continue
      </Button>
    </div>
  );
}

function ProductsStep({
  products,
  selected,
  onToggle,
  onNext,
  canProceed,
}: {
  products: typeof PRODUCTS;
  selected: UserIntent[];
  onToggle: (id: UserIntent) => void;
  onNext: () => void;
  canProceed: boolean;
}) {
  const allSelected = products.every(p => selected.includes(p.id));

  const handleSelectAll = () => {
    if (allSelected) {
      products.forEach(p => {
        if (selected.includes(p.id)) onToggle(p.id);
      });
    } else {
      products.forEach(p => {
        if (!selected.includes(p.id)) onToggle(p.id);
      });
    }
  };

  return (
    <div className="text-left space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Select your interests
        </h1>
        <p className="text-lg text-muted-foreground">
          Choose how you'd like to use Reklaim
        </p>
      </div>

      {/* Select All */}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={handleSelectAll}
      >
        <Check className="w-4 h-4" />
        {allSelected ? 'Deselect all' : 'Select all'}
      </Button>

      {/* Product Cards */}
      <div className="space-y-3">
        {products.map((product) => {
          const isSelected = selected.includes(product.id);
          return (
            <Button
              key={product.id}
              type="button"
              variant="ghost"
              onClick={() => onToggle(product.id)}
              /* No `aria-label` -- the accessible name composes from the
               * visible product title + badge + description text. Per
               * Copilot's R2 review: an aria-label here would override
               * the inner content and prevent screen readers from
               * exposing the badge / description structure. */
              aria-pressed={isSelected}
              className={`h-auto w-full items-start justify-start gap-4 whitespace-normal rounded-lg border-2 p-4 text-left font-normal transition-colors hover:text-foreground ${
                isSelected
                  ? 'border-primary bg-primary/5 hover:bg-primary/5'
                  : 'border-border bg-background hover:border-muted-foreground hover:bg-background'
              }`}
            >
              <div className={`shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {product.icon}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-medium text-foreground">{product.title}</span>
                  {product.badge && (
                    <span className={`text-sm font-medium px-2 py-0.5 rounded-md ${product.badgeColor}`}>
                      {product.badge}
                    </span>
                  )}
                </div>
                <p className="text-base text-muted-foreground mt-1">{product.description}</p>
              </div>

              <div className={`shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
              }`}>
                {isSelected && <Check className="w-4 h-4 text-primary-foreground" aria-hidden="true" />}
              </div>
            </Button>
          );
        })}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Interested in all of the above? Great! Tap continue to proceed.
      </p>

      <Button
        size="lg"
        onClick={onNext}
        disabled={!canProceed}
        className="w-full h-14 text-lg"
      >
        Continue
      </Button>
    </div>
  );
}

/**
 * Synchronous age validator. The canonical age rules live in `onboardingSchema`
 * (`src/lib/schemas/auth.ts`) but we need a synchronous result for the
 * disabled-state of the Continue button. This mirrors the schema logic; if
 * the schema's age range or messages change, update both.
 */
function validateAge(month: string, day: string, year: string): { valid: boolean; error: string | null } {
  if (!month || !day || !year || year.length !== 4) return { valid: false, error: null };

  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  const y = parseInt(year, 10);

  if (Number.isNaN(m) || Number.isNaN(d) || Number.isNaN(y)) return { valid: false, error: 'Please enter a valid date.' };

  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return { valid: false, error: 'Please enter a valid date.' };
  }

  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() - (m - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age--;

  if (age < 18) return { valid: false, error: 'You must be at least 18 years old to sign up.' };
  if (age > 85) return { valid: false, error: 'Please enter a valid date of birth.' };

  return { valid: true, error: null };
}


export function EmailStep({
  firstName,
  lastName,
  email,
  emailReadOnly,
  onClearReadOnly,
  dobMonth,
  dobDay,
  dobYear,
  ageConfirmed,
  marketingConsent,
  referralCode = '',
  onReferralCodeChange,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onDobMonthChange,
  onDobDayChange,
  onDobYearChange,
  onAgeConfirmedChange,
  onMarketingConsentChange,
  onSubmit,
  isLoading,
  error,
}: {
  firstName: string;
  lastName: string;
  email: string;
  emailReadOnly?: boolean;
  onClearReadOnly?: () => void;
  dobMonth: string;
  dobDay: string;
  dobYear: string;
  ageConfirmed: boolean;
  marketingConsent: boolean;
  referralCode?: string;
  onReferralCodeChange?: (v: string) => void;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onDobMonthChange: (v: string) => void;
  onDobDayChange: (v: string) => void;
  onDobYearChange: (v: string) => void;
  onAgeConfirmedChange: (v: boolean) => void;
  onMarketingConsentChange: (v: boolean) => void;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
}) {
  // Expand the referral field by default when a code arrived via the `?ref=`
  // link (so link users can see/confirm/change it); otherwise keep it tucked
  // behind a "Have a referral code?" toggle to keep the form short for the
  // majority (REK-1480).
  // Capture the exact ISO timestamp when the age/terms checkbox is ticked so
  // we record the moment of genuine consent, not the moment a social-auth
  // redirect fires (which can be seconds later).
  const consentTimestampRef = useRef<string | null>(null);
  const [showReferralInput, setShowReferralInput] = useState<boolean>(() => !!referralCode);
  // Only validate/block on what the user actually edits. A code pre-seeded from
  // the `?ref=` link or stale `attribution.ref` must NOT block Continue if it
  // happens to be malformed (e.g. a hand-edited URL) -- it is simply ignored
  // (parent's `effectiveReferralCode` is null for malformed and clears it from
  // storage on submit). Once the user types, validation kicks in (REK-1480, R2).
  const [referralTouched, setReferralTouched] = useState(false);
  const emailValid = email.includes('@');
  const ageValidation = validateAge(dobMonth, dobDay, dobYear);
  const nameValid = firstName.trim().length > 0 && lastName.trim().length > 0;
  // Referral code is optional, but if the user typed something it must be a
  // well-formed code (8 hex chars). A malformed non-empty code shows an inline
  // hint and blocks Continue so a mistyped code is fixed (or cleared) rather
  // than silently dropped (REK-1480).
  const trimmedReferral = referralCode.trim().toUpperCase();
  const referralCodeError =
    referralTouched && trimmedReferral.length > 0 && !REFERRAL_CODE_PATTERN.test(trimmedReferral)
      ? "That doesn't look like a valid referral code — please double-check it."
      : null;
  const isValid = emailValid && ageValidation.valid && ageConfirmed && nameValid && !referralCodeError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && !isLoading) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="text-left space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Enter your details
        </h1>
        <p className="text-lg text-muted-foreground">
          We'll use this to create your account
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="text"
            aria-label="First name"
            placeholder="First name *"
            required
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            disabled={isLoading}
            className="h-14 text-lg px-4 border-2"
          />
          <Input
            type="text"
            aria-label="Last name"
            placeholder="Last name *"
            required
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            disabled={isLoading}
            className="h-14 text-lg px-4 border-2"
          />
        </div>

        <div>
          <Input
            type="email"
            aria-label="Email address"
            placeholder="Email address"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            readOnly={emailReadOnly}
            disabled={isLoading}
            className={`h-14 text-lg px-4 border-2 ${emailReadOnly ? 'bg-muted cursor-not-allowed' : ''}`}
            autoFocus={!emailReadOnly}
          />
          {emailReadOnly && onClearReadOnly && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onClearReadOnly}
              className="mt-1 px-0 h-auto"
            >
              Not you? Change email
            </Button>
          )}
        </div>

        {/* Date of Birth -- shared <DateField> (native <input type="date">),
            the same control used by the date question in every wizard. We tried
            a Radix Select (the original signup blocker, REK-1649) and a Popover +
            shadcn Calendar, but both render poorly on small touch screens. The
            native input delegates to the OS date picker (iOS wheel / Android
            calendar), which cannot overflow the viewport. The native value IS
            already `YYYY-MM-DD`, which we split back into the existing MM/DD/YYYY
            state, so `validateAge()` and the `reklaim_dob` localStorage format are
            unchanged. `min`/`max` restrict the picker to the 18-85 window;
            `validateAge()` is the safety net for manually-typed dates. */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-foreground">Date of Birth</legend>
          {(() => {
            const dobBounds = getDobBounds();
            const dobValue =
              dobMonth && dobDay && dobYear.length === 4
                ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`
                : '';
            return (
              <DateField
                aria-label="Date of birth"
                autoComplete="bday"
                value={dobValue}
                min={dobBounds.min}
                max={dobBounds.max}
                onChange={(v) => {
                  if (!v) {
                    onDobYearChange('');
                    onDobMonthChange('');
                    onDobDayChange('');
                    return;
                  }
                  const [y, m, d] = v.split('-');
                  onDobYearChange(y);
                  onDobMonthChange(String(Number(m)));
                  onDobDayChange(String(Number(d)));
                }}
                disabled={isLoading}
                className="h-14 text-lg px-4 border-2"
              />
            );
          })()}
          <p className="text-xs text-muted-foreground">
            We use your date of birth to verify eligibility and improve your experience.
          </p>
          {ageValidation.error && (
            <p className="text-sm text-destructive">{ageValidation.error}</p>
          )}
        </fieldset>

        {/* Age confirmation checkbox -- shadcn/Radix Checkbox renders a
            <button>, not an <input>, so a wrapping <label> won't toggle it.
            Use an explicit id + htmlFor so clicking the visible text dispatches
            to the checkbox, and so assistive tech reads the visible label. */}
        <div className="flex items-start gap-3">
          <Checkbox
            id="age-confirm"
            checked={ageConfirmed}
            onCheckedChange={(checked) => {
              const isChecked = checked === true;
              consentTimestampRef.current = isChecked ? new Date().toISOString() : null;
              onAgeConfirmedChange(isChecked);
            }}
            disabled={isLoading}
            className="mt-0.5"
          />
          <label
            htmlFor="age-confirm"
            className="text-sm text-muted-foreground leading-snug cursor-pointer select-none"
          >
            I confirm I am 18 years of age or older
          </label>
        </div>

        {/* REK-1727: Marketing-consent checkbox -- NOT required to proceed
            (deliberately excluded from `isValid` per the ticket: "If they
            don't check the box - don't add them to Braze"). When checked,
            the Unveil-complete step fires `unveil-braze action=tag` so the
            user lands in the "Reklaim Unveil Start" Braze segment. */}
        <div className="flex items-start gap-3">
          <Checkbox
            id="marketing-consent"
            checked={marketingConsent}
            onCheckedChange={(checked) => onMarketingConsentChange(checked === true)}
            disabled={isLoading}
            className="mt-0.5"
          />
          <label
            htmlFor="marketing-consent"
            className="text-sm text-muted-foreground leading-snug cursor-pointer select-none"
          >
            I agree to receive marketing emails, promotions, and product updates from Reklaim. I can unsubscribe at any time.
          </label>
        </div>

        {/* REK-1480: optional referral-code entry. A friend's code no longer
            requires the `?ref=` link -- users who heard a code word-of-mouth
            can type it here and both sides get bonus entries. Tucked behind a
            toggle so the form stays short; auto-expanded when a code arrived
            via the link. NOT required to proceed (excluded from `isValid`). */}
        {!showReferralInput ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => setShowReferralInput(true)}
            disabled={isLoading}
            className="px-0 h-auto"
          >
            Have a referral code?
          </Button>
        ) : (
          <div>
            <Input
              type="text"
              aria-label="Referral code"
              placeholder="Referral code (optional)"
              value={referralCode}
              onChange={(e) => {
                setReferralTouched(true);
                onReferralCodeChange?.(e.target.value.toUpperCase().replace(/\s+/g, ''));
              }}
              disabled={isLoading}
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={8}
              aria-invalid={!!referralCodeError}
              aria-describedby={referralCodeError ? 'referral-code-error' : 'referral-code-hint'}
              className={`h-14 text-lg px-4 border-2 ${referralCodeError ? 'border-destructive' : ''}`}
            />
            {referralCodeError ? (
              <p id="referral-code-error" className="mt-1 text-sm text-destructive">{referralCodeError}</p>
            ) : (
              <p id="referral-code-hint" className="mt-1 text-xs text-muted-foreground">
                Got a friend&apos;s code? Enter it to earn bonus entries for you both.
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="text-base text-destructive">{error}</p>
        )}
      </div>

      <Button
        type="submit"
        size="lg"
        disabled={!isValid || isLoading}
        className="w-full h-14 text-lg"
      >
        {isLoading ? (
          <>
            <Spinner className="size-5 mr-2" />
            Checking...
          </>
        ) : (
          'Continue'
        )}
      </Button>

      <SocialAuthButtons
          leadingDivider
          redirectUrl="/auth/sso-callback"
          redirectUrlComplete="/onboarding"
          requireTermsAccepted
          termsAccepted={ageConfirmed}
          disabled={isLoading}
          onBeforeRedirect={() => {
            if (!ageConfirmed) return false;
            storeSsoTermsAcceptance(consentTimestampRef.current ?? new Date().toISOString());
          }}
          onStartFailed={() => {
            // Clear the stash if the redirect never leaves this page — otherwise
            // a later login-stage social click could reuse a stale affirmation.
            consumeSsoTermsAcceptance();
          }}
        />

      <p className="text-center text-xs text-muted-foreground -mt-2">
          By signing up with a social account, you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            Terms and Conditions
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            Privacy Policy
          </a>
          .
        </p>

      {/* Exit option */}
      <p className="text-center text-sm text-muted-foreground mt-4">
        Not ready?{' '}
        <Link to="/" className="text-primary hover:underline">
          Come back later
        </Link>
      </p>
    </form>
  );
}

function LoadingStep() {
  return (
    <div className="text-center space-y-6 py-12">
      <div className="relative w-20 h-20 mx-auto">
        <div className="absolute inset-0 rounded-full border-2 border-muted" />
        <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
      <p className="text-lg text-muted-foreground">Scanning for your data...</p>
    </div>
  );
}

function RevealStep({
  email,
  breachData,
  profileData,
  entriesAwarded,
  calculation,
  brandCount,
  dataBrokerCount,
  accountAttributes,
  hasUnveilData,
  isLoading,
  onClaim,
}: {
  email: string;
  breachData: { name: string; severity: string; dataTypes: string[] }[];
  profileData: { key: string; value: string }[];
  entriesAwarded: number;
  calculation: {
    base: number;
    breachBonus: number;
    profileBonus: number;
    criticalBonus: number;
  };
  brandCount: number;
  dataBrokerCount: number;
  accountAttributes: AccountAttributes | null;
  hasUnveilData: boolean;
  isLoading: boolean;
  onClaim: () => void;
}) {
  const hasBreaches = breachData.length > 0;
  const earnEntries = calculation.base + calculation.profileBonus;
  const [showScanResults, setShowScanResults] = useState(false);

  const [displayCount, setDisplayCount] = useState(0);

  const fireCelebrationConfetti = useCallback(() => {
    const duration = 2000;
    const end = Date.now() + duration;
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#ffffff'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#ffffff'],
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  useEffect(() => {
    const duration = 1200;
    const steps = 30;
    const stepValue = earnEntries / steps;
    const stepDuration = duration / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += stepValue;
      if (current >= earnEntries) {
        setDisplayCount(earnEntries);
        clearInterval(timer);
        fireCelebrationConfetti();
      } else {
        setDisplayCount(Math.floor(current));
      }
    }, stepDuration);
    return () => clearInterval(timer);
  }, [earnEntries, fireCelebrationConfetti]);

  return (
    <div className="text-left space-y-8 pb-40 lg:pb-8">
      {/* HERO SECTION — above the fold */}
      <div className="opacity-0 animate-fade-up grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        {/* Left: headline + CTA */}
        <div className="space-y-6 text-center lg:text-left">
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-foreground leading-tight">
            You have <span className="text-primary">{displayCount}</span> jackpot entries waiting!
          </h1>
          <p className="text-lg text-muted-foreground">
            Claim your entries and win weekly and monthly jackpots ranging from <span className="font-semibold text-foreground">$1,000</span> to <span className="font-semibold text-foreground">$10,000</span>.
          </p>

          {/* REK-1986 (SSO.5): Clerk smart CAPTCHA mount. signUp.create()
              (fired by the claim CTA via useFastAuth.sendOtp) renders its
              challenge widget here when it decides to challenge; otherwise
              the div stays empty. Clerk-flag builds only — the legacy
              Supabase DOM is unchanged. Resends re-use the in-progress
              sign-up (prepare only), so this reveal-step mount is the only
              one needed. */}
          <div id="clerk-captcha" className="flex justify-center lg:justify-start" />

          {/* Desktop CTA */}
          <div className="hidden lg:block space-y-3">
            <Button
              size="lg"
              onClick={() => {
                localStorage.setItem('reklaim_terms_accepted_at', new Date().toISOString());
                onClaim();
              }}
              disabled={isLoading}
              className="w-full h-14 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              {isLoading ? (
                <>
                  <Spinner className="size-5 mr-2" />
                  Sending code...
                </>
              ) : (
                <>
                  Claim Your Entries
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              No purchase necessary · Sign up is free
            </p>
            <p className="text-center text-xs text-muted-foreground">
              By signing up, you agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms and Conditions</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>

        {/* Right: Jackpot card */}
        <div className="opacity-0 animate-fade-up animate-stagger-1">
          <div className="relative overflow-hidden bg-linear-to-br from-amber-400 via-yellow-400 to-amber-500 text-amber-950 rounded-2xl shadow-xl p-6">
            <div className="shimmer-overlay pointer-events-none rounded-2xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5" />
                <span className="font-bold text-sm uppercase tracking-wide">Jackpot</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-xs font-medium opacity-80">Next Jackpot</p>
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-black">$5,000</p>
                </div>
                <div className="text-right">
                  <CountdownTimer />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-amber-600/30 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-medium">Weekly &amp; Monthly Jackpots $1,000 - $10,000</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TWO CARDS — below the fold */}
      <div className="opacity-0 animate-fade-up animate-stagger-2 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Earn More Entries card */}
        <div className="bg-linear-to-br from-gray-50 to-gray-100 dark:from-muted/50 dark:to-muted/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-500" />
            </div>
            <h3 className="text-xl font-bold text-foreground">Earn More Entries</h3>
          </div>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500 shrink-0" />
              <span>Quick surveys = more entries</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500 shrink-0" />
              <span>Earn points for every answer</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500 shrink-0" />
              <span>Weekly $1K + monthly jackpots</span>
            </div>
          </div>
          <div className="pt-3 border-t border-border/50">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span className="text-sm font-medium text-foreground">
                Complete your profile to earn up to 190 additional points
              </span>
            </div>
          </div>
        </div>

        {/* See if your data is exposed card */}
        <div className="bg-linear-to-br from-gray-50 to-gray-100 dark:from-muted/50 dark:to-muted/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-500" />
            </div>
            <h3 className="text-xl font-bold text-foreground">See if your data is exposed</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Scan to find out which data brokers have your personal info.
          </p>

          {!showScanResults ? (
            <Button
              type="button"
              size="lg"
              onClick={() => setShowScanResults(true)}
              className="w-full rounded-xl font-bold text-base shadow-md"
            >
              Scan Now
            </Button>
          ) : (
            <div className="space-y-3 animate-fade-up">
              <div className="bg-linear-to-br from-orange-500/10 to-red-500/10 rounded-xl border border-orange-500/30 p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <span className="font-bold text-foreground">
                    {hasBreaches ? `${breachData.length} Breaches` : 'No Breaches'}
                  </span>
                </div>
                {(hasBreaches || dataBrokerCount > 0) && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-red-600 dark:text-red-400">
                    <Database className="w-3 h-3" />
                    <span>{dataBrokerCount > 0 ? dataBrokerCount : `~${Math.min(breachData.length * 3, 25)}`} data brokers may have your info</span>
                  </div>
                )}
              </div>
              {hasBreaches && (
                <div className="max-h-32 overflow-y-auto space-y-1 rounded-lg border border-border/50 p-2">
                  {breachData.map((breach, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50">
                      <span className="text-foreground truncate">{breach.name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        breach.severity === 'critical' ? 'bg-red-500/20 text-red-600' :
                        breach.severity === 'high' ? 'bg-orange-500/20 text-orange-600' :
                        'bg-yellow-500/20 text-yellow-600'
                      }`}>
                        {breach.severity}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="opacity-0 animate-fade-up animate-stagger-3 text-center space-y-1 hidden lg:block">
        <p className="text-sm text-muted-foreground">No purchase necessary · Sign up is free</p>
        <p className="text-xs text-muted-foreground">
          By signing up, you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms and Conditions</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>
        </p>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-lg border-t border-border safe-area-inset-bottom z-50">
        <div className="flex items-center justify-center gap-2 mb-2 text-sm">
          <Trophy className="w-4 h-4 text-amber-500" />
          <span className="font-medium text-foreground">$1K Weekly Jackpot</span>
        </div>
        <Button
          size="lg"
          onClick={() => {
            localStorage.setItem('reklaim_terms_accepted_at', new Date().toISOString());
            onClaim();
          }}
          disabled={isLoading}
          className={`w-full h-14 rounded-2xl font-bold text-lg shadow-xl ${isLoading ? '' : 'animate-bounce-subtle'}`}
        >
          {isLoading ? (
            <>
              <Spinner className="size-5 mr-2" />
              Sending code...
            </>
          ) : (
            <>
              Claim Your Entries
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-2">
          No purchase necessary · Sign up is free
        </p>
        <p className="text-center text-xs text-muted-foreground mt-1">
          By signing up, you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}

function MagicLinkStep({
  email,
  onResend,
  onVerifyOtp,
  isLoading,
  error,
  resendCooldown,
}: {
  email: string;
  onResend: () => void;
  onVerifyOtp: (code: string) => void;
  isLoading: boolean;
  error: string | null;
  resendCooldown: number;
}) {
  const [otpCode, setOtpCode] = useState('');
  // D-2 (REK-1986): the Clerk verification email carries a 6-digit code ONLY
  // (no magic link), so the code input shows immediately under the flag.
  const [showOtpInput, setShowOtpInput] = useState<boolean>(true);

  // Cross-tab magic-link click handling moved to the parent component, which
  // watches isAuthenticated from useAuth() and routes to /dashboard.

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length === 6) {
      onVerifyOtp(otpCode);
    }
  };

  return (
    <div className="text-left space-y-6">
      <h1 className="text-3xl lg:text-4xl font-medium text-foreground text-center sm:text-left">
        Check your email
      </h1>

      <AuthEmailSentPanel
        email={email}
        intent="sign-up"
        resendCooldown={resendCooldown}
        isLoading={isLoading}
        onResend={onResend}
        error={error}
        onEnterCodeInstead={!showOtpInput ? () => setShowOtpInput(true) : undefined}
      >
        {showOtpInput ? (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <label className="sr-only">Enter the 6-digit code from your email</label>
            <InputOTP
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              value={otpCode}
              onChange={(value) => setOtpCode(value.replace(/\D/g, '').slice(0, 6))}
              onComplete={(code) => {
                const cleaned = code.replace(/\D/g, '').slice(0, 6);
                if (cleaned.length === 6 && !isLoading) {
                  onVerifyOtp(cleaned);
                }
              }}
              disabled={isLoading}
              autoFocus
              containerClassName="justify-center"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>

            <Button
              type="submit"
              size="lg"
              disabled={otpCode.length !== 6 || isLoading}
              className="w-full h-14 text-lg"
            >
              {isLoading ? (
                <>
                  <Spinner className="size-5 mr-2" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </Button>
          </form>
        ) : null}
      </AuthEmailSentPanel>

      <p className="text-xs text-center text-muted-foreground">
        By creating an account, you also agree to receive important updates about your account security and data.
      </p>

      {/* Exit option */}
      <p className="text-center text-sm text-muted-foreground mt-4">
        Changed your mind?{' '}
        <Link to="/" className="text-primary hover:underline">
          I'll finish later
        </Link>
      </p>
    </div>
  );
}

function CreatingStep() {
  return (
    <div className="text-center space-y-6 py-12">
      <Spinner className="size-12 text-primary mx-auto" />
      <p className="text-lg text-muted-foreground">
        Creating your account...
      </p>
    </div>
  );
}

// REK-1903: brief interstitial while we check whether the prefilled email
// already has an account. Neutral copy (does not reveal the existence probe).
function CheckingStep() {
  return (
    <div className="text-center space-y-6 py-12">
      <Spinner className="size-12 text-primary mx-auto" />
      <p className="text-lg text-muted-foreground">
        Just a moment...
      </p>
    </div>
  );
}

function CompleteStep({ earnEntries }: { earnEntries: number }) {
  return (
    <div className="text-center space-y-6 py-12">
      <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center mx-auto">
        <Check className="w-10 h-10 text-primary" />
      </div>
      <div className="space-y-2">
        <p className="text-2xl font-medium text-foreground">
          Account created!
        </p>
        <p className="text-lg text-muted-foreground">
          You earned <span className="font-medium text-primary">{earnEntries} entries</span>
        </p>
      </div>
    </div>
  );
}

// ProfileUnlockStep was extracted to ./ProfileUnlockStep.tsx (REK-1730 QA) so
// the one-click sign-up flow (AuthSignup.tsx) can reuse the same screen.
